import { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { Pagination } from '@/shared/components/custom/pagination';
import { ClientMembersToolbar } from '../components/ClientMembersToolbar';
import { InviteClientMemberDialog } from '../components/InviteClientMemberDialog';
import { useClientMembers } from '../hooks/useClientMembers';
import { useDeleteClientMember } from '../hooks/useDeleteClientMember';
import { useClient } from '@/features/clients/public';
import { getClientMemberColumns } from '../adapters/clientMemberColumns';
import { useToast } from '@/shared/hooks/useToast';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { computeVisiblePages } from '@/shared/utils/paginationUtils';
import type { UsePaginationReturn } from '@/shared/types/pagination';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';

/**
 * Manages the member list for a client with invite and delete actions.
 *
 * Requires the `clientId` route param to be present.
 * Handles four visual states: loading, error, empty, and populated.
 * Requires `activeOrganizationId` to be defined (obtained via internal hooks).
 *
 * @remarks
 * `useClientMembers` is called once with pagination params derived from local state.
 *
 * @example
 * <ClientMembersPage />
 */
export function ClientMembersPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { state } = useLocation();
  const stateClientName = (state as { clientName?: string } | null)?.clientName;
  const client = useClient(clientId!);
  const clientName = client?.name ?? stateClientName;
  const { t } = useTranslation('client-members');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState({ pageIndex: 0, pageSize: 10 });

  const membersData = useClientMembers(
    clientId!,
    search,
    paginationState.pageIndex,
    paginationState.pageSize,
  );

  const deleteClientMember = useDeleteClientMember(clientId!);
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const members = membersData.data;
  const isLoading = membersData.isLoading;
  const isError = membersData.isError;
  const totalItems = membersData.totalCount ?? 0;

  const totalPages = totalItems > 0 ? Math.ceil(totalItems / paginationState.pageSize) : 0;
  const canPrevious = paginationState.pageIndex > 0;
  const canNext = totalPages > 0 && paginationState.pageIndex < totalPages - 1;
  const visiblePages = computeVisiblePages(paginationState.pageIndex, totalPages);

  const goToPage = (page: number) => {
    if (totalPages === 0) return;
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: Math.max(0, Math.min(page, totalPages - 1)),
    }));
  };
  const next = () =>
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: totalPages > 0 ? Math.min(prev.pageIndex + 1, totalPages - 1) : prev.pageIndex,
    }));
  const previous = () =>
    setPaginationState((prev) => ({ ...prev, pageIndex: Math.max(prev.pageIndex - 1, 0) }));
  const first = () => setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  const last = () => {
    if (totalPages > 0) setPaginationState((prev) => ({ ...prev, pageIndex: totalPages - 1 }));
  };
  const changePageSize = (size: number) => setPaginationState({ pageIndex: 0, pageSize: size });

  const pagination: UsePaginationReturn = {
    pageIndex: paginationState.pageIndex,
    pageSize: paginationState.pageSize,
    totalItems,
    totalPages,
    visiblePages,
    canPrevious,
    canNext,
    goToPage,
    next,
    previous,
    first,
    last,
    changePageSize,
  };

  const activeFiltersCount = search ? 1 : 0;

  const handleResetFilters = () => {
    setSearch('');
  };

  const handleDelete = async (memberId: string) => {
    const member = members.find((m) => m.id === memberId);
    if (!member) return;

    const result = await confirm({
      title: t('confirm.delete'),
      description: t('confirm.deleteDesc', { name: member.displayName }),
      confirmLabel: t('confirm.confirm'),
      cancelLabel: t('confirm.cancel'),
      variant: 'destructive',
    });

    if (!result.confirmed) return;

    setDeletingMemberId(memberId);
    try {
      await deleteClientMember.mutateAsync(memberId);
      toast.success({
        title: t('toast.deleted'),
        description: t('toast.deletedDesc', { name: member.displayName }),
      });
    } catch {
      // global handler shows error toast
    }
    setDeletingMemberId(null);
  };

  const columns = getClientMemberColumns({
    onDelete: handleDelete,
    isDeleteLoading: (memberId: string) => deletingMemberId === memberId,
    t,
  });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div>
        {isLoading ? (
          <div data-testid="client-members-loading-skeleton">
            <ClientMembersToolbar
              clientName={clientName}
              search={search}
              onSearchChange={setSearch}
              activeFiltersCount={activeFiltersCount}
              onResetFilters={handleResetFilters}
              onInviteClick={() => setIsInviteDialogOpen(true)}
            />
            <div className="space-y-4 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-16 bg-muted/50 rounded animate-pulse" />
              ))}
            </div>
          </div>
        ) : isError ? (
          <div
            data-testid="client-members-error-message"
            className="p-4 text-center text-destructive"
          >
            {t('toast.errorDesc')}
          </div>
        ) : (
          <div>
            <ClientMembersToolbar
              clientName={clientName}
              search={search}
              onSearchChange={setSearch}
              activeFiltersCount={activeFiltersCount}
              onResetFilters={handleResetFilters}
              onInviteClick={() => setIsInviteDialogOpen(true)}
            />

            {members.length === 0 ? (
              <div
                data-testid="client-members-empty-state"
                className="p-8 text-center text-muted-foreground"
              >
                {t('noMembersFound')}
              </div>
            ) : (
              <ListView
                data={members}
                columns={columns}
                getRowId={(member) => member.id}
                data-testid="client-members-list-table"
              />
            )}

            <Pagination {...pagination} />
          </div>
        )}

        <InviteClientMemberDialog
          clientId={clientId!}
          open={isInviteDialogOpen}
          onOpenChange={setIsInviteDialogOpen}
        />

        {isOpen && options && (
          <ConfirmDialog
            open={isOpen}
            onOpenChange={(open) => !open && handleCancel()}
            title={options.title}
            description={options.description}
            confirmLabel={options.confirmLabel}
            cancelLabel={options.cancelLabel}
            variant={options.variant}
            onConfirm={handleConfirm}
          />
        )}
      </div>
    </FeatureErrorBoundary>
  );
}
